import * as Schema from "effect/Schema";
import { EnvironmentId, TrimmedNonEmptyString } from "./baseSchemas.ts";

export const CloudEnvironmentStatus = Schema.Literals([
  "provisioning",
  "ready",
  "suspending",
  "suspended",
  "deleting",
  "deleted",
  "failed",
]);
export const CloudEnvironmentOperation = Schema.Literals(["create", "suspend", "resume", "delete"]);
export const CloudEnvironmentAction = Schema.Literals(["retry", "suspend", "resume", "delete"]);
export type CloudEnvironmentAction = typeof CloudEnvironmentAction.Type;
export const CloudUpdaterStatus = Schema.Literals([
  "idle",
  "updating",
  "ready",
  "rolled_back",
  "failed",
]);

export const CloudEnvironment = Schema.Struct({
  environmentId: EnvironmentId,
  region: TrimmedNonEmptyString,
  resourceSize: TrimmedNonEmptyString,
  desiredVersion: TrimmedNonEmptyString,
  desiredReleaseOperationId: Schema.NullOr(TrimmedNonEmptyString),
  observedVersion: Schema.NullOr(TrimmedNonEmptyString),
  desiredImage: Schema.NullOr(TrimmedNonEmptyString),
  observedImage: Schema.NullOr(TrimmedNonEmptyString),
  updateStatus: CloudUpdaterStatus,
  updateFailure: Schema.NullOr(TrimmedNonEmptyString),
  updaterLastSeenAt: Schema.NullOr(TrimmedNonEmptyString),
  status: CloudEnvironmentStatus,
  operation: CloudEnvironmentOperation,
  operationId: TrimmedNonEmptyString,
  health: Schema.Literals(["unknown", "healthy", "unhealthy"]),
  lastHealthyAt: Schema.NullOr(TrimmedNonEmptyString),
  failureReason: Schema.NullOr(TrimmedNonEmptyString),
  maxConcurrentAgents: Schema.Int.check(Schema.isGreaterThan(0)),
  createdAt: TrimmedNonEmptyString,
  updatedAt: TrimmedNonEmptyString,
});
export type CloudEnvironment = typeof CloudEnvironment.Type;

export const CloudEnvironmentCreateRequest = Schema.Struct({});
export const CloudEnvironmentActionRequest = Schema.Struct({
  action: CloudEnvironmentAction,
  expectedOperationId: TrimmedNonEmptyString,
});
export const CloudEnrollmentCredential = Schema.Struct({
  environmentId: EnvironmentId,
  operationId: TrimmedNonEmptyString,
  credential: TrimmedNonEmptyString,
});
export const CloudEnrollmentRequest = Schema.Struct({
  ...CloudEnrollmentCredential.fields,
  proof: TrimmedNonEmptyString,
});
export const CloudEnrollmentReadyRequest = Schema.Struct({
  ...CloudEnrollmentCredential.fields,
  environmentCredential: TrimmedNonEmptyString,
});
export const CloudUpdaterRequestHeaders = Schema.Struct({ authorization: TrimmedNonEmptyString });
export const CloudUpdaterDesiredRequest = Schema.Struct({ environmentId: EnvironmentId });
export type CloudUpdaterDesiredRequest = typeof CloudUpdaterDesiredRequest.Type;
export const CloudUpdaterDesiredResponse = Schema.Struct({
  environmentId: EnvironmentId,
  operationId: TrimmedNonEmptyString,
  version: TrimmedNonEmptyString,
  image: TrimmedNonEmptyString,
  manifestUrl: TrimmedNonEmptyString,
  signatureBundleUrl: TrimmedNonEmptyString,
  manifest: Schema.optional(Schema.String),
  signatureBundle: Schema.optional(Schema.String),
});
export const CloudUpdaterReportRequest = Schema.Struct({
  environmentId: EnvironmentId,
  operationId: TrimmedNonEmptyString,
  status: Schema.Literals(["updating", "ready", "rolled_back", "failed"]),
  observedImage: Schema.String,
  failureReason: Schema.NullOr(Schema.String),
});
export type CloudUpdaterReportRequest = typeof CloudUpdaterReportRequest.Type;

export class CloudEnvironmentForbidden extends Schema.TaggedErrorClass<CloudEnvironmentForbidden>()(
  "CloudEnvironmentForbidden",
  { reason: Schema.Literals(["billing_required", "admin_required", "size_not_allowed"]) },
  { httpApiStatus: 403 },
) {}
export class CloudEnvironmentNotFound extends Schema.TaggedErrorClass<CloudEnvironmentNotFound>()(
  "CloudEnvironmentNotFound",
  {},
  { httpApiStatus: 404 },
) {}
export class CloudEnvironmentConflict extends Schema.TaggedErrorClass<CloudEnvironmentConflict>()(
  "CloudEnvironmentConflict",
  { reason: Schema.Literals(["invalid_transition", "stale_operation", "profile_not_configured"]) },
  { httpApiStatus: 409 },
) {}
export class CloudEnrollmentInvalid extends Schema.TaggedErrorClass<CloudEnrollmentInvalid>()(
  "CloudEnrollmentInvalid",
  { reason: Schema.Literals(["invalid", "expired", "consumed", "not_ready"]) },
  { httpApiStatus: 401 },
) {}
